import type { AgentTableRow, CsAgent } from '~/components/SmsVerifyStats/smsVerifyStatsUtils';

import type { AiSummaryStatsData } from './types';

type ChartDataPoint = {
  date: string;
  totalCount: number;
};

function extractAllAgentEmails(csAgents: Array<CsAgent>): Set<string> {
  const emails = new Set<string>();

  function traverse(agents: Array<CsAgent>) {
    for (const agent of agents) {
      if (agent.Email) {
        emails.add(agent.Email.toLowerCase());
      }

      if (agent.subordinates?.length) {
        traverse(agent.subordinates);
      }
    }
  }

  traverse(csAgents);
  return emails;
}

export function transformAiSummaryStats(data: AiSummaryStatsData, filterEmails?: Set<string>): Array<ChartDataPoint> {
  if (!data?.result?.dailyCounts) {
    return [];
  }

  const dateCountMap = new Map<string, number>();

  for (const agent of data.result.dailyCounts) {
    const normalizedEmail = agent.email?.toLowerCase();

    if (filterEmails && (!normalizedEmail || !filterEmails.has(normalizedEmail))) {
      continue;
    }

    for (const dailyCount of agent.dailyCounts) {
      const currentCount = dateCountMap.get(dailyCount.date) || 0;
      dateCountMap.set(dailyCount.date, currentCount + dailyCount.count);
    }
  }

  return Array.from(dateCountMap.entries())
    .map(([date, totalCount]) => ({
      date,
      totalCount,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function transformAiSummaryStatsForTable(
  data: AiSummaryStatsData,
  csAgents: Array<CsAgent>,
): Array<AgentTableRow> {
  const allAgentEmails = extractAllAgentEmails(csAgents);
  const availableDates = Array.from(
    new Set(
      (data?.result?.dailyCounts || []).flatMap((agent) => agent.dailyCounts.map((dailyCount) => dailyCount.date)),
    ),
  ).sort((a, b) => a.localeCompare(b));

  const dateRange = availableDates.map((date, index) => ({
    day: availableDates.length - index,
    date,
  }));

  const statsByEmail = new Map<string, Map<string, number>>();
  if (data?.result?.dailyCounts) {
    for (const agent of data.result.dailyCounts) {
      const email = agent.email?.toLowerCase();
      if (!email) {
        continue;
      }

      const dateCountMap = new Map<string, number>();
      for (const dailyCount of agent.dailyCounts) {
        dateCountMap.set(dailyCount.date, dailyCount.count);
      }
      statsByEmail.set(email, dateCountMap);
    }
  }

  const agentRows = Array.from(allAgentEmails)
    .sort((a, b) => a.localeCompare(b))
    .map((email) => {
      const agentStats = statsByEmail.get(email) || new Map<string, number>();
      const dayCounts = dateRange.map(({ day, date }) => ({
        day,
        date,
        count: agentStats.get(date) || 0,
      }));

      return {
        email,
        agentId: '',
        dayCounts,
        total: dayCounts.reduce((sum, dayCount) => sum + dayCount.count, 0),
      };
    });

  for (const agent of data?.result?.dailyCounts || []) {
    const email = agent.email?.toLowerCase();
    if (email && allAgentEmails.has(email)) {
      continue;
    }

    const agentStats = new Map(agent.dailyCounts.map((dailyCount) => [dailyCount.date, dailyCount.count]));
    const dayCounts = dateRange.map(({ day, date }) => ({
      day,
      date,
      count: agentStats.get(date) || 0,
    }));

    agentRows.push({
      email: email || agent.agentId || 'unknown-agent',
      agentId: agent.agentId || '',
      dayCounts,
      total: dayCounts.reduce((sum, dayCount) => sum + dayCount.count, 0),
    });
  }

  return agentRows.sort((a, b) => a.email.localeCompare(b.email));
}
